class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector('details');
    this.content = this.mainDetailsToggle.querySelector('summary').nextElementSibling;

    this.mainDetailsToggle.addEventListener('focusout', this.onFocusOut.bind(this));
    this.mainDetailsToggle.addEventListener('toggle', this.onToggle.bind(this));
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onToggle() {
    if (!this.animations) this.animations = this.content.getAnimations();

    if (this.mainDetailsToggle.hasAttribute('open')) {
      this.animations.forEach((animation) => animation.play());
    } else {
      this.animations.forEach((animation) => animation.cancel());
    }
  }

  close() {
    this.mainDetailsToggle.removeAttribute('open');
    this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', false);
  }
}

customElements.define('details-disclosure', DetailsDisclosure);

class HeaderMenu extends DetailsDisclosure {
  constructor() {
    super();
    this.header = document.querySelector('.header-wrapper');
    this.isBigMenu = this.mainDetailsToggle.classList.contains('mega-menu--big');
    this.hoverCloseDelay = 220;
    this.closeTimer = null;
    this.carousel = null;
    this.carouselToggle = null;

    if (this.isBigMenu) {
      this.setupHoverBehavior();
      this.setupCarouselToggle();
    }
  }

  setupHoverBehavior() {
    this.summary = this.mainDetailsToggle.querySelector('summary');

    this.addEventListener('mouseenter', this.onHoverOpen.bind(this));
    this.addEventListener('mouseleave', this.onHoverClose.bind(this));

    // En escritorio (con hover) el control es por hover, no por click
    if (this.summary) {
      this.summary.addEventListener('click', (event) => {
        if (this.canHover()) event.preventDefault();
      });
    }
  }

  canHover() {
    return window.matchMedia('(hover: hover)').matches;
  }

  clearCloseTimer() {
    if (this.closeTimer) {
      clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }
  }

  onHoverOpen() {
    if (!this.canHover()) return;
    this.clearCloseTimer();

    if (!this.mainDetailsToggle.open) {
      this.mainDetailsToggle.setAttribute('open', '');
      if (this.summary) this.summary.setAttribute('aria-expanded', true);
    }
  }

  onHoverClose() {
    if (!this.canHover()) return;
    this.clearCloseTimer();
    this.closeTimer = setTimeout(() => this.close(), this.hoverCloseDelay);
  }

  setupCarouselToggle() {
    this.carousel = this.mainDetailsToggle.querySelector('[data-carousel]');
    this.carouselToggle = this.mainDetailsToggle.querySelector('[data-carousel-toggle]');

    if (!this.carouselToggle) return;

    this.carouselToggle.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.clearCloseTimer();
      this.toggleCarouselExpanded();
    });
  }

  toggleCarouselExpanded() {
    const isExpanded = this.mainDetailsToggle.classList.toggle('is-carousel-expanded');
    this.carousel?.classList.toggle('is-expanded', isExpanded);
    this.carouselToggle.setAttribute('aria-expanded', String(isExpanded));

    const labelExpanded = this.carouselToggle.dataset.labelExpanded;
    const labelCollapsed = this.carouselToggle.dataset.labelCollapsed;
    this.carouselToggle.textContent = isExpanded ? labelCollapsed : labelExpanded;
  }

  resetCarouselExpanded() {
    this.mainDetailsToggle.classList.remove('is-carousel-expanded');
    this.carousel?.classList.remove('is-expanded');

    if (!this.carouselToggle) return;

    this.carouselToggle.setAttribute('aria-expanded', 'false');
    this.carouselToggle.textContent = this.carouselToggle.dataset.labelExpanded;
  }

  close() {
    this.resetCarouselExpanded();
    super.close();
  }

  onToggle() {
    if (!this.header) return;
    this.header.preventHide = this.mainDetailsToggle.open;

    if (!this.mainDetailsToggle.open) {
      this.resetCarouselExpanded();
    }

    if (this.mainDetailsToggle.open && this.isBigMenu) {
      this.updateBridgeHeight();
      this.restartBigMenuAnimation();
    }

    if (document.documentElement.style.getPropertyValue('--header-bottom-position-desktop') !== '') return;
    document.documentElement.style.setProperty(
      '--header-bottom-position-desktop',
      `${Math.floor(this.header.getBoundingClientRect().bottom)}px`
    );
  }

  // Calcula el alto del "puente" invisible entre el item del menu y el borde
  // inferior del header para que el hover no se pierda al navegar hacia abajo.
  updateBridgeHeight() {
    if (!this.summary || !this.header) return;
    const headerBottom = this.header.getBoundingClientRect().bottom;
    const summaryBottom = this.summary.getBoundingClientRect().bottom;
    const gap = Math.max(0, Math.floor(headerBottom - summaryBottom));
    this.mainDetailsToggle.style.setProperty('--mega-bridge-height', `${gap}px`);
  }

  restartBigMenuAnimation() {
    const items = this.mainDetailsToggle.querySelectorAll(
      '.mega-menu__list--big > li, .mega-menu__promos--big > .mega-menu__promo-card, .mega-menu__carousel-list--primary > .mega-menu__carousel-item, .mega-menu__carousel-button'
    );
    if (!items.length) return;

    items.forEach((item) => {
      item.style.animation = 'none';
    });

    // Forzar reflow para que la animacion se reinicie en cada apertura
    void this.mainDetailsToggle.offsetWidth;

    items.forEach((item) => {
      item.style.animation = '';
    });
  }
}

customElements.define('header-menu', HeaderMenu);
